import type { ActiveGiftPromotionsQuery, CurrencyCode, GiftConditionMetric, GiftConditionScope, GiftItemKind, GiftPromotionRole, OrderFragment } from '#graphql-operations'
import { colorHex, isNearWhite } from '../constants/colorAliases'

/**
 * Gift with order — the storefront's wording, picked from the rule's shape.
 *
 * Pure: every helper here answers WHICH message to show and with WHAT values;
 * the caller translates it (`translateGiftCopy`, or `<I18nT>` with `keypath`,
 * the params as slots and `plural`). Money is formatted by the caller's
 * `usePriceFormatter()`, handed in as `money` so each helper formats the one
 * figure that belongs in its message. Backend money is in minor units of the
 * rule's currency; quantity figures are units.
 *
 * The admin copy (`headline`, `body`, `badge`) is not handled here: a
 * CAMPAIGN promotion shows it verbatim, and on the other styles a non-empty
 * `headline` overrides the template — that choice belongs to the component.
 */

/** A message key with its named params and, for a pipe-plural message, the count. */
export interface GiftCopy {
  key: string
  params: Record<string, string | number>
  /** Set only for keys with plural forms; pass it as vue-i18n's plural argument. */
  plural?: number
}

/** Formats minor units — pass `usePriceFormatter()` as is. */
export type GiftMoneyFormatter = (amount: number, currencyCode: CurrencyCode) => string

/** One promotion from `activeGiftPromotions`. */
export type GiftPromotion = ActiveGiftPromotionsQuery['activeGiftPromotions'][number]
/** A preview of one gift option (promotion `giftOptions`/`conditionProducts`, progress `giftOptions`). */
export type GiftOptionPreview = GiftPromotion['giftOptions'][number]
/** One rule's progress on the active order (`Order.giftProgress`). */
export type GiftProgressEntry = OrderFragment['giftProgress'][number]
/** One rule with gift units still to choose (`Order.pendingGifts`). */
export type PendingGift = OrderFragment['pendingGifts'][number]
/** One entry of a pending rule's gift pool. */
export type PendingGiftOption = PendingGift['options'][number]
/** One colour (its own product) of a pending gift option. */
export type PendingGiftColour = PendingGiftOption['colours'][number]
/** One size of that colour, with its cart-aware availability. */
export type PendingGiftVariant = PendingGiftColour['variants'][number]

/** An optional offer can stay unclaimed when the customer checks out. */
export function giftRequiresSelection(gift: Pick<PendingGift, 'optional' | 'remaining'>): boolean {
  return !gift.optional && gift.remaining > 0
}

/** What the helpers read off a gift option — either a preview or a pending option. */
interface GiftOptionLike {
  kind: GiftItemKind
  product: { name: string }
  /** On previews. */
  colourCount?: number
  /** On pending options. */
  colours?: readonly unknown[]
}

interface GiftCollectionLike {
  name: string
  genderFacetValues: readonly { code: string }[]
}

type GiftNameTranslator = (key: string, params: Record<string, string | number>) => string

/** The condition part every rule shape shares. */
interface GiftConditionLike {
  scope: GiftConditionScope
  metric: GiftConditionMetric
  repeat: boolean
  maxGifts: number
  collection?: { name: string } | null
}

/** Translates a `GiftCopy` — the plural argument only when the message has plural forms. */
export function translateGiftCopy(t: ReturnType<typeof useI18n>['t'], copy: GiftCopy): string {
  return copy.plural === undefined
    ? t(copy.key, copy.params)
    : t(copy.key, copy.params, copy.plural)
}

/**
 * The `{product}` of a message: the gift's own name when the pool has one
 * option, "a gift of your choice" when the customer picks from several.
 * An empty pool (every option's product deleted) reads as a choice too,
 * rather than as an empty name in the middle of a sentence.
 */
export function giftProductCopy(options: readonly GiftOptionLike[], rewardMode?: GiftPromotion['rewardMode'], collection = ''): GiftCopy | { text: string } {
  if (rewardMode === 'CHEAPEST_IN_COLLECTION') {
    return collection.trim()
      ? { key: 'gift.copy.cheapest_in_collection', params: { collection } }
      : { key: 'gift.copy.cheapest_eligible_item', params: {} }
  }
  const [only] = options
  if (options.length === 1 && only)
    return { text: only.product.name }
  return { key: 'gift.copy.gift_of_choice', params: {} }
}

/** `giftProductCopy`, translated — the value to pass as `product`. */
export function giftProductName(t: GiftNameTranslator, options: readonly GiftOptionLike[], rewardMode?: GiftPromotion['rewardMode'], collection?: GiftCollectionLike | null): string {
  const copy = giftProductCopy(options, rewardMode, rewardMode === 'CHEAPEST_IN_COLLECTION' ? giftCollectionName(t, collection) : '')
  return 'text' in copy ? copy.text : t(copy.key, copy.params)
}

/**
 * The `{collection}` of a message: the collection's name and who it is for —
 * "Všechny topy pro muže". Women's and men's collections share names, and a
 * rule counts only the one it names, so the bare name leaves the customer
 * guessing which half of the shop to buy from. One gender (`men` / `women`)
 * is named; anything else — both, unisex, or no gender filter at all — lets
 * either in and says so. Empty without a collection. The prize e-mail follows
 * the same rule (`collectionAudienceOf` in the backend).
 */
export function giftCollectionName(
  t: GiftNameTranslator,
  collection: GiftCollectionLike | null | undefined,
): string {
  if (!collection?.name)
    return ''
  const genders = collection.genderFacetValues
  const only = genders.length === 1 ? genders[0]?.code : undefined
  const audience = only === 'men' || only === 'women' ? only : 'all'
  return t(`gift.copy.collection_for_${audience}`, { collection: collection.name })
}

/** How many colours an option offers, from whichever shape it comes in. */
function colourCountOf(option: GiftOptionLike): number {
  return option.colourCount ?? option.colours?.length ?? 1
}

/**
 * The quiet line under the gift saying what the customer will choose:
 * several options → "Choose from 3 gifts"; one DESIGN option with more than
 * one live colour → colour and size; one PRODUCT option (or a DESIGN with a
 * single colour left) → size; a fixed VARIANT → nothing to choose, null.
 */
export function giftChoiceHint(options: readonly GiftOptionLike[]): GiftCopy | null {
  if (options.length > 1)
    return { key: 'gift.copy.choose_from', params: { count: options.length }, plural: options.length }
  const [only] = options
  if (!only || only.kind === 'VARIANT')
    return null
  if (only.kind === 'DESIGN' && colourCountOf(only) > 1)
    return { key: 'gift.copy.you_choose_colour_size', params: {} }
  return { key: 'gift.copy.you_choose_size', params: {} }
}

/** "Repeats with every threshold, up to N gifts" — only for a repeating rule that can grant more than one. */
export function giftRepeatHint(rule: Pick<GiftConditionLike, 'repeat' | 'maxGifts'>): GiftCopy | null {
  if (!rule.repeat || rule.maxGifts <= 1)
    return null
  return { key: 'gift.copy.repeat', params: { count: rule.maxGifts }, plural: rule.maxGifts }
}

/**
 * The key segment for a condition: `collection_*` needs the collection's name,
 * so a COLLECTION rule whose collection is gone falls back to the
 * `products_*` wording ("selected items"), which stays true.
 */
function conditionSegment(rule: GiftConditionLike): 'all' | 'collection' | 'products' {
  if (rule.scope === 'COLLECTION')
    return rule.collection?.name ? 'collection' : 'products'
  return rule.scope === 'PRODUCTS' ? 'products' : 'all'
}

/** Which of a promotion's roles the PDP speaks for; see `giftPromotionCopy`. */
export type GiftPromotionVoice = 'qualifies' | 'gift' | 'same'

/**
 * The role the product page speaks in when a promotion names the product in
 * both roles. A PRODUCTS rule counting units of a product that is also its
 * gift is a "buy 2, get 1 free" offer and says so. Otherwise the gift role
 * wins — on the gift's own page (every product qualifies for an ALL rule, and
 * a collection rule's gift is usually in that collection) "get this free
 * when …" is the relevant news.
 */
export function giftPromotionVoice(promotion: Pick<GiftPromotion, 'roles' | 'scope' | 'metric' | 'rewardMode'>): GiftPromotionVoice {
  // No individual PDP can promise its product will be the cheapest in a future cart.
  if (promotion.rewardMode === 'CHEAPEST_IN_COLLECTION')
    return 'qualifies'
  const roles: readonly GiftPromotionRole[] = promotion.roles
  const gift = roles.includes('GIFT')
  if (!gift)
    return 'qualifies'
  if (roles.includes('QUALIFIES') && promotion.scope === 'PRODUCTS' && promotion.metric === 'QUANTITY')
    return 'same'
  return 'gift'
}

/**
 * The condition sentence of a PDP promotion (`gift.promo.qualifies.*` /
 * `gift.promo.gift.*`), e.g. "Free tote with orders over 1 500 Kč" or "Get
 * this free when you buy 2 from Knitwear". `product` is the translated gift
 * name (`giftProductName`), `collection` the translated collection
 * (`giftCollectionName`). `voice` defaults to `giftPromotionVoice`. The
 * wording follows `promotionCondition`.
 */
export function giftPromotionCopy(
  promotion: GiftPromotion,
  values: { product: string, collection: string, money: GiftMoneyFormatter },
  voice: GiftPromotionVoice = giftPromotionVoice(promotion),
): GiftCopy {
  const condition = promotionCondition(promotion, values)
  const params = { ...condition.params, product: values.product }

  if (promotion.rewardMode === 'CHEAPEST_IN_COLLECTION')
    return { key: 'gift.promo.cheapest_in_collection', params, plural: condition.params.count }

  if (voice === 'same')
    return { key: 'gift.promo.qualifies.quantity_same', params, plural: condition.params.count }

  const role = voice === 'gift' ? 'gift' : 'qualifies'
  return { key: `gift.promo.${role}.${condition.wording}`, params, plural: condition.plural }
}

/**
 * The short condition set right of a promotion row's title (`gift.promo.condition.*`):
 * "Over 1 500 Kč", "With every order", "Buy 2 from Knitwear". Null in the
 * "buy 2, get 1 free" voice, where the offer's own sentence is the title and
 * says the condition already. `collection` as for `giftPromotionCopy`.
 */
export function giftPromotionConditionCopy(
  promotion: GiftPromotion,
  values: { collection: string, money: GiftMoneyFormatter },
  voice: GiftPromotionVoice = giftPromotionVoice(promotion),
): GiftCopy | null {
  if (promotion.rewardMode === 'CHEAPEST_IN_COLLECTION') {
    const count = Math.max(1, promotion.minQuantity)
    return { key: 'gift.promo.cheapest_in_collection', params: { count, collection: values.collection }, plural: count }
  }
  if (voice === 'same')
    return null
  const condition = promotionCondition(promotion, values)
  return { key: `gift.promo.condition.${condition.wording}`, params: condition.params, plural: condition.plural }
}

/**
 * Which wording a promotion's condition takes — the last key segment shared by
 * `gift.promo.{qualifies,gift,condition}.*` — with its parameters, and the
 * plural count for the wordings that have one.
 *
 * A QUANTITY rule with a threshold of one unit that does not repeat is the
 * "with every order" offer. A VALUE rule without a threshold for this
 * currency reads like the ALL/QUANTITY rule it degrades to on the backend.
 * `products_quantity` is the one wording the catalogue did not have for the
 * PDP ("Buy 2 selected items, get … free").
 */
function promotionCondition(promotion: GiftPromotion, values: { collection: string, money: GiftMoneyFormatter }): {
  wording: string
  params: { amount: string, collection: string, count: number }
  plural?: number
} {
  const count = Math.max(1, promotion.minQuantity)
  const amount = promotion.thresholdWithTax == null
    ? ''
    : values.money(Number(promotion.thresholdWithTax), promotion.currencyCode)
  const params = { amount, collection: values.collection, count }
  const segment = conditionSegment(promotion)
  const isValue = promotion.metric === 'VALUE' && amount !== ''

  if (segment === 'all') {
    if (isValue)
      return { wording: 'value', params }
    if (count <= 1 && !promotion.repeat)
      return { wording: 'always', params }
    return { wording: 'quantity', params, plural: count }
  }
  return isValue
    ? { wording: `${segment}_value`, params }
    : { wording: `${segment}_quantity`, params, plural: count }
}

/**
 * Where a rule stands inside its current step — for a repeating rule, the
 * distance between the last unit earned and the next one. `remaining` is the
 * backend's (to the next unit, 0 once capped or, without repeat, reached), so
 * the step's own progress is `threshold - remaining`. Units for QUANTITY,
 * minor units for VALUE. `percent` is 0–100, rounded like the free-shipping
 * bar.
 */
export function giftProgressStep(entry: Pick<GiftProgressEntry, 'threshold' | 'remaining' | 'unlocked'>): {
  current: number
  threshold: number
  remaining: number
  percent: number
} {
  const threshold = Math.max(0, entry.threshold)
  const remaining = Math.min(threshold, Math.max(0, entry.remaining))
  if (threshold === 0) {
    const done = entry.unlocked ? 1 : 0
    return { current: done, threshold: 1, remaining: 1 - done, percent: done * 100 }
  }
  const current = threshold - remaining
  return { current, threshold, remaining, percent: Math.min(100, Math.round((current / threshold) * 100)) }
}

/**
 * Nothing more to earn from this rule on this order: the repeat cap is
 * reached, or a non-repeating rule already unlocked its one gift.
 */
export function giftProgressIsComplete(entry: Pick<GiftProgressEntry, 'capped' | 'unlocked' | 'repeat'>): boolean {
  return entry.capped || (entry.unlocked && !entry.repeat)
}

/** What `GiftPromoProgress` says about one rule; `giftProgressState` picks it. */
export type GiftProgressState = 'claim' | 'added' | 'step'

/**
 * Which progress state one rule is in on the active order, or null when there
 * is nothing true to say (no qualifying item yet, or a reached rule whose gift
 * could not be given because every option is sold out):
 * - `claim` — a gift unit is waiting to be chosen;
 * - `added` — the gift is on the order and there is nothing more to earn, or
 *   a repeating rule has just started its next step with nothing towards it
 *   yet ("0 of 2" under a gift already in the bag would read as taken away);
 * - `step` — part of the way there.
 */
export function giftProgressState(
  entry: Pick<GiftProgressEntry, 'pending' | 'granted' | 'capped' | 'unlocked' | 'repeat' | 'threshold' | 'remaining'>,
): GiftProgressState | null {
  if (entry.pending > 0)
    return 'claim'
  if (giftProgressIsComplete(entry) || giftProgressStep(entry).current <= 0)
    return entry.granted > 0 ? 'added' : null
  return 'step'
}

/**
 * The cart's progress line for one rule (`gift.progress.*`):
 *
 * - capped → "You've unlocked the maximum of N free gifts"
 * - reached, no repeat → "Free gift unlocked: …"
 * - reached, repeating → "Spend X more for another free gift" / "Add N more …"
 * - not reached → the distance and the gift, worded by scope and metric.
 *
 * `amount` is the remaining money (VALUE), `count` the remaining units
 * (QUANTITY); `product` is the translated gift name (`giftProductName`),
 * `collection` the translated collection (`giftCollectionName`).
 */
export function giftProgressCopy(
  entry: GiftProgressEntry,
  values: { product: string, collection: string, money: GiftMoneyFormatter },
): GiftCopy {
  const remaining = Math.max(0, entry.remaining)
  const isValue = entry.metric === 'VALUE'
  const amount = isValue ? values.money(remaining, entry.currencyCode) : ''
  const params = { product: values.product, amount, collection: values.collection, count: remaining }

  if (entry.rewardMode === 'CHEAPEST_IN_COLLECTION' && entry.pending > 0)
    return { key: 'gift.promo.row.product_free', params }

  // A reached rule that can give only one gift (no repeat, or a cap of 1) is
  // also `capped` on the wire, so it is checked first: "maximum of 1 free
  // gift" would be a strange way to say the gift is unlocked.
  if (entry.unlocked && (!entry.repeat || entry.maxGifts <= 1))
    return { key: 'gift.progress.unlocked', params }
  if (entry.capped)
    return { key: 'gift.progress.capped', params: { ...params, count: entry.maxGifts }, plural: entry.maxGifts }
  if (entry.unlocked) {
    return isValue
      ? { key: 'gift.progress.next_value', params }
      : { key: 'gift.progress.next_quantity', params, plural: remaining }
  }

  const segment = conditionSegment(entry)
  if (segment === 'all') {
    return isValue
      ? { key: 'gift.progress.value', params }
      : { key: 'gift.progress.quantity', params, plural: remaining }
  }
  return isValue
    ? { key: `gift.progress.${segment}_value`, params }
    : { key: `gift.progress.${segment}_quantity`, params, plural: remaining }
}

/**
 * The live progress line under a PDP promotion (`gift.promo.progress.*`):
 * a unit waiting to be chosen → "Unlocked — choose your gift in the bag";
 * nothing more to earn and a gift on the order → "Your free gift is in the
 * bag"; otherwise the step so far — "1 of 2 in your bag" (QUANTITY) or
 * "350 Kč to go" (VALUE).
 */
export function giftPromoProgressCopy(entry: GiftProgressEntry, money: GiftMoneyFormatter): GiftCopy {
  if (entry.pending > 0)
    return { key: 'gift.promo.progress.unlocked', params: {} }
  if (entry.granted > 0 && giftProgressIsComplete(entry))
    return { key: 'gift.promo.progress.added', params: {} }
  const step = giftProgressStep(entry)
  if (entry.metric === 'VALUE')
    return { key: 'gift.promo.progress.value', params: { amount: money(step.remaining, entry.currencyCode) } }
  return { key: 'gift.promo.progress.quantity', params: { current: step.current, threshold: step.threshold } }
}

/**
 * A gift colour's label and swatch. The label is the product's `color` facet
 * value as the backend sends it (trimmed — some values carry trailing
 * whitespace), falling back to the code. The swatch resolves the
 * locale-independent code with its dashes stripped, exactly like
 * `orderLineColorSwatch`, then the label squashed the same way; `colorHex`
 * answers `#cccccc` for a shade it does not know, so the dot degrades to
 * neutral grey. `nearWhite` tells the caller to ring the dot with `--line`.
 */
export function giftColourSwatch(choice: { colorCode?: string | null, colorLabel?: string | null }): {
  label: string
  hex: string
  nearWhite: boolean
} {
  const code = choice.colorCode?.trim() ?? ''
  const label = choice.colorLabel?.trim() || code
  const lookup = (code || label).replace(/[\s-]/g, '')
  const hex = colorHex(lookup)
  return { label, hex, nearWhite: isNearWhite(hex) }
}

/**
 * The checkout's note under a gift a "buy N, get one free" rule put on the order: returning some of
 * the items it was earned with means returning the gift too ("If you return any items from
 * Leggings for women, you'll also need to return the free gift."). Only a counted rule (`QUANTITY`)
 * that takes two or more items: under a value threshold, or a gift with every order, returning one
 * item need not undo the offer. Only once the gift is on the order (`granted`). Empty otherwise.
 */
export function giftReturnNote(
  t: ReturnType<typeof useI18n>['t'],
  entry: Pick<GiftProgressEntry, 'metric' | 'threshold' | 'granted' | 'scope' | 'collection'>,
): string {
  if (entry.metric !== 'QUANTITY' || entry.threshold < 2 || entry.granted <= 0)
    return ''
  const collection = entry.scope === 'COLLECTION' ? giftCollectionName(t, entry.collection) : ''
  return collection
    ? t('checkout.gift.return_note.collection', { collection })
    : t('checkout.gift.return_note.items')
}
